<template>
  <div class="add-bank-container">
    <div>
      <bread-crumbs
        :page="$t('bank.list.page')"
        :section="
          isActionUpdate() ? getTruncatedBankName : $t('bank.addBank.header')
        "
        :pageClick="openListOfBank"
      />
      <div class="spacing-32"></div>
    </div>
    <div>
      <page-header
        :title="
          isActionUpdate()
            ? $t('bank.addBank.headerUpdate')
            : $t('bank.addBank.header')
        "
      />
      <div class="spacing-12"></div>
      <div
        v-if="isActionUpdate()"
        class="drip-body font-weight-400 color-noble-blue-500"
      >
        {{ $t('bank.addBank.form.subtext') }}
      </div>
    </div>
    <div>
      <template v-if="bankLoading">
        <loader :height="300" :style="'width: 500px'" />
      </template>
      <template v-else>
        <template v-if="isActionUpdate()">
          <div class="spacing-32"></div>
          <div class="d-flex align-items-center drip-h2 color-noble-blue-500">
            {{ getTruncatedBankName }}
            <chip
              :class="'ml-3 bank-status ' + getAccountStatus(bank.status)"
              :chipText="getAccountStatus(bank.status)"
            />
          </div>
          <div class="spacing-32"></div>
        </template>
        <template v-else>
          <div class="spacing-24"></div>
        </template>
        <bank-form
          v-if="isActionUpdate()"
          :bank="bank"
          :submitLoading="submitLoading"
          :uploadsSaved="uploadsSaved"
          @addBankAccount="updateBankAccount"
          :buttonText="$t('bank.addBank.form.buttonUpdate')"
        />
        <bank-form
          v-else
          :bank="bank"
          :uploadsSaved="uploadsSaved"
          :submitLoading="submitLoading"
          @addBankAccount="addBankAccount"
          :buttonText="$t('bank.addBank.form.buttonAddBank')"
        />
      </template>
    </div>
  </div>
</template>

<script>
import bankService from '@/services/bank'
import Loader from '@/components/common/Loader'
import PageHeader from '@/components/common/PageHeader'
import BreadCrumbs from '@/components/common/BreadCrumbs'
import BankForm from '@/components/bank-accounts/BankForm'
import Chip from '@/components/common/Chip'
import { ACTIONS, ENTITIES } from '@/utils/constants'
import { uploadFilesBus } from '../../main'

export default {
  props: ['prev'],
  errorCaptured: function (err) {
    console.log('Caught error', err.message)
  },
  components: {
    Chip,
    PageHeader,
    Loader,
    BankForm,
    BreadCrumbs
  },
  beforeCreate() {
    const bid = this.$route.query.bid || this.$route.params.bid
    if (
      !bid &&
      !this.$store.getters.hasActionPermission(ENTITIES.BANK, ACTIONS.CREATE)
    ) {
      this.$notify({
        title: this.$t('error.unauthorizedPage'),
        type: 'error'
      })
      this.$router.push({ name: 'bankAccounts' })
    }
  },
  data() {
    return {
      bank: {},
      bankLoading: false,
      submitLoading: false,
      banks: [],
      uploadsSaved: [
        {
          index: 0,
          editable: true,
          visible: true,
          vIndex: 0,
          exporter_user_id: '',
          pod_docs: [],
          pci_docs: [],
          pop_docs: [],
          bank_supporting_document: [],
          errorMessage: ''
        }
      ]
    }
  },
  created() {
    uploadFilesBus.$on('addDocument', this.addDocument)
    uploadFilesBus.$on('removeDocument', this.removeDocument)
    uploadFilesBus.$on('documentRequired', this.documentRequired)
  },
  mounted() {
    const bid = this.$route.query.bid || this.$route.params.bid
    if (bid) {
      this.fetchBankDetails(bid)
    }
  },
  computed: {
    getTruncatedBankName() {
      if (this.bank.bank_name) {
        if (this.bank.bank_name && this.bank.bank_name.length > 24) {
          return this.bank.bank_name.slice(0, 24) + '...'
        } else {
          return this.bank.bank_name
        }
      }
      return ''
    }
  },
  methods: {
    documentRequired(show) {
      if (show) {
        this.uploadsSaved[0].errorMessage = this.$t(
          'bank.addBank.form.uploads.bank_supporting_document.errorMessage'
        )
      } else {
        this.uploadsSaved[0].errorMessage = ''
      }
    },
    getAccountStatus(status) {
      return status === 'verified' ? 'Verified' : 'Verification Pending'
    },
    addDocument(data) {
      if (data && data.docTag && data.id && this.uploadsSaved[0][data.docTag]) {
        this.uploadsSaved[0][data.docTag].push({
          docId: data.id, // doc id
          id: '', // task id
          name: data.name,
          url: data.url,
          tag: data.tag
        })
        this.documentRequired(false)
        this.$mpTrack('Add Bank - File uploaded (by type)', {
          tag: data.docTag
        })
      }
    },
    removeDocument(documentIndex, docTag, documentId) {
      if (
        documentIndex >= 0 &&
        docTag &&
        this.uploadsSaved[documentIndex][docTag] &&
        documentId
      ) {
        if (this.bank.id) {
          bankService
            .removeBankDocument(
              this.$store.getters.accountId,
              this.bank.id,
              documentId
            )
            .then((res) => {
              this.uploadsSaved[documentIndex][docTag] = this.uploadsSaved[
                documentIndex
              ][docTag].filter((x) => x.id !== documentId)
              this.$mpTrack('Add Bank - File removed (by type)', {
                tag: docTag
              })
            })
            .catch((e) => {
              this.$notify({
                title:
                  e?.response?.data?.error || this.$t('error.unableToProcess'),
                type: 'error'
              })
            })
        } else {
          bankService
            .removeExporterDocument(this.$store.getters.accountId, documentId)
            .then((res) => {
              this.uploadsSaved[documentIndex][docTag] = this.uploadsSaved[
                documentIndex
              ][docTag].filter((x) => x.id !== documentId)
              this.$mpTrack('Add Bank - File removed (by type)', {
                tag: docTag
              })
            })
            .catch((e) => {
              this.$notify({
                title:
                  e?.response?.data?.error || this.$t('error.unableToProcess'),
                type: 'error'
              })
            })
        }
      }
    },
    isActionUpdate() {
      return this.$route.params.bid
    },
    fetchBankDetails(bankId) {
      this.bankLoading = true
      bankService
        .fetchBankDetails(this.$store.getters.token, bankId)
        .then((res) => {
          if (res && res.data) {
            this.bank = res.data
            this.beneficiary = res.data.beneficiary_name
            this.uploadsSaved[0].bank_supporting_document =
              res.data.supporting_documents
          }
          this.bankLoading = false
        })
        .catch((e) => {
          console.log(e)
          this.bankLoading = false
        })
    },
    openListOfBank() {
      this.$mpTrack(`Add Bank - Navigate to bank list`)
      this.$router.push({ name: 'bankAccounts' })
    },
    addBankAccount(formData) {
      this.$mpTrack(`Add bank - Complete bank addition clicked`)
      this.submitLoading = true
      bankService
        .addBank(this.$store.getters.accountId, formData)
        .then((res) => {
          if (res.data && res.data.message === 'Exporter Bank Details Added') {
            this.$mpTrack(`Add bank - New bank details updated`)
            this.submitLoading = false
            this.$notify({
              title: this.$t('bank.addBank.successAddBank'),
              type: 'success'
            })
            if (this.prevRoute === 'viewBank') {
              this.$router.push({
                name: 'viewBank',
                params: {
                  bid: this.$route.params.sid,
                  ignoreDialog: true
                }
              })
              return
            }
            this.$router.push({
              name: 'bankAccounts'
            })
          }
        })
        .catch((e) => {
          this.submitLoading = false
          this.$notify({
            title: e?.response?.data?.error || this.$t('error.unableToProcess'),
            type: 'error'
          })
        })
    },
    updateBankAccount(formData) {
      this.$mpTrack(`Add bank - Update bank details button clicked`)
      this.submitLoading = true
      bankService
        .updateBank(this.$store.getters.accountId, formData)
        .then((res) => {
          if (res.data) {
            this.$mpTrack(`Add bank - Existing bank details updated`)
            this.submitLoading = false
            this.$notify({
              title: this.$t('bank.addBank.successUpdateBank'),
              type: 'success'
            })
            this.$router.push({
              name: 'viewBank',
              params: {
                bid: this.$route.query.bid || this.$route.params.bid,
                ignoreDialog: true
              }
            })
          }
        })
        .catch((e) => {
          this.submitLoading = false
          this.$notify({
            title: e?.response?.data?.error || this.$t('error.unableToProcess'),
            type: 'error'
          })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.add-bank-container {
  padding: 40px 108px;
  @media screen and (max-width: 1200px) {
    padding: 40px 56px;
  }
  @media screen and (max-width: 768px) {
    padding: 40px 24px;
  }
}
</style>
